/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './add-payment-modal.scss';
import {
  Box,
  Dialog,
  DialogContent,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  ReservePaymentType,
  useUpsertReservePaymentMutation,
  ReservePaymentInput,
  ReservePayment,
  Attachments,
  AttachmentUploadInput,
  UploadFolderName,
  Attachment,
  useSelectedVendorsAndContactListQuery,
  useGetFolderList,
  DeleteNoteAttachment,
  useAlaePaymentCoveragesQuery,
  useCloseClaimDetailsQuery,
  useLossPaymentCoveragesQuery,
  useUlaePaymentCoveragesQuery,
  ClaimantType,
} from '@converge/graphql';
// import { useReservesQuery } from '@converge/graphql';
import {
  IUploadFile,
  NewConvergeButton,
  NewFormTextField,
  NewTypeAhead,
  FormNumberInputAdornmentField,
  FileUploadDropzone,
  NewFromSelectField,
} from '../../../../presentation/components';
import CloseIcon from '@mui/icons-material/Close';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useParams, useOutletContext } from 'react-router-dom';
import { checkPositiveNumber } from '@converge/utils';
import { useConvergeSnackBar } from '../../../../contexts';
import { IClaimDetailsProps } from '../claim-details';

const paymentExceedsReservesError = 'Payments cannot exceed reserves';
const retentionExceedsRemainingError =
  'Retention payment cannot exceed remaining retention';
const oneAttachmentRequired = 'At least one attachment is required';

// Define the schema for payment validation
const getPaymentSchema = (
  reserveType: string,
  isEditing: boolean,
  originalRetentionAmount: number,
  remainingRetention: number,
  isUlae: boolean,
  dynamicRetention: number,
  isPrimaryPolicy: boolean
) => {
  return yup.object({
    reserveAmount: yup.number().when([], {
      is: () =>
        (isPrimaryPolicy && (remainingRetention ?? 0) > 0) ||
        (remainingRetention === 0 && originalRetentionAmount),
      then: () => yup.number().optional(),
      otherwise: () =>
        yup
          .number()
          .typeError('Payment amount is required')
          .positive('Payment amount is required')
          .required('Amount is required'),
    }),
    invoiceNumber: yup.string().when('payeeType', {
      is: (payeeType: string) =>
        payeeType === 'vendor' &&
        (reserveType === 'ALAE' ||
          reserveType === 'ULAE' ||
          reserveType === 'LOSS'),
      then: () =>
        yup
          .string()
          .required('Invoice number is required')
          .max(21, 'Invoice number must be 21 characters or fewer'),
      otherwise: () =>
        yup
          .string()
          .optional()
          .max(21, 'Invoice number must be 21 characters or fewer'),
    }),
    invoiceDate: yup.string().required('Invoice date is required'),
    paymentDate: yup.string().required('Payment date is required'),
    type: yup.string().when([], {
      is: () =>
        ((remainingRetention ?? 0) > 0 ||
          (isEditing && remainingRetention) ||
          dynamicRetention > 0) &&
        !isUlae &&
        isPrimaryPolicy,
      then: () => yup.string().optional().nullable(),
      otherwise: () => yup.string().required('Payment method is required'),
    }),
    vendor: yup.string().when('payeeType', {
      is: (payeeType: string) =>
        payeeType === 'vendor' &&
        (reserveType === 'ALAE' ||
          reserveType === 'ULAE' ||
          reserveType === 'LOSS'),
      then: () => yup.string().required('Vendor is required'),
      otherwise: () => yup.string().optional(),
    }),
    claimant: yup.string().when('payeeType', {
      is: (payeeType: string) => payeeType === 'claimant',
      then: () => yup.string().required('Claimant is required'),
      otherwise: () => yup.string().optional(),
    }),
    careOf: yup.string().optional(),
    mailingAddress: yup.string().optional(),
    coverage: yup.string().when([], {
      is: () => reserveType !== 'ULAE',
      then: () => yup.string().required('Coverage is required'),
      otherwise: () => yup.string().nullable().optional(),
    }),
    subCoverage: yup.string().nullable().optional(),
    paymentMethod: yup.string().optional(),
    message: yup.string().optional(), // Updated field name to match API
    attachments: yup
      .array()
      .min(1, oneAttachmentRequired)
      .required('Attachment is required'), // For file attachments
    folder: yup.string().optional(), // Folder for uploaded files
    expenseType: yup.string().when([], {
      is: () => reserveType === 'ALAE' || reserveType === 'ULAE',
      then: () => yup.string().required('Expense type is required'),
      otherwise: () => yup.string().optional(),
    }),
    lossType: yup.string().when([], {
      is: () => reserveType === 'LOSS',
      then: () => yup.string().required('Loss type is required'),
      otherwise: () => yup.string().optional(),
    }),
    subExpenseType: yup.string().nullable().optional(),
    retentionPayment: yup
      .number()
      .max(remainingRetention)
      .when([], {
        is: () =>
          ((remainingRetention ?? 0) > 0 ||
            (isEditing &&
              !remainingRetention &&
              originalRetentionAmount > 0)) &&
          !isUlae &&
          isPrimaryPolicy,
        then: () =>
          yup
            .number()
            .typeError('Payment amount is required')
            .positive('Payment amount is required')
            .required('Payment amount is required'),
        otherwise: () => yup.number().optional().nullable(),
      }),
    payeeType: yup.string().when([], {
      is: () => reserveType === 'ALAE' || reserveType === 'LOSS',
      then: () => yup.string().required('Payee is required'),
      otherwise: () => yup.string().optional(),
    }),
  });
};

export interface PaymentFormData {
  reserveAmount: number;
  invoiceNumber: string;
  invoiceDate: string;
  paymentDate: string;
  type: ReservePaymentType;
  vendor?: string;
  claimant?: string;
  payee?: string;
  careOf?: string;
  mailingAddress?: string;
  coverage?: string | null;
  subCoverage?: string | null; // Sub coverage for detailed categorization
  message?: string; // Updated to match API field name
  attachments?: IUploadFile[]; // For file attachments
  expenseType?: string; // Expense type for ALAE payments
  lossType?: string; // Loss type for Loss payments
  subExpenseType?: string | null; // Sub expense type for ALAE payments
  payeeType?: string; // Payee type: 'vendor' or 'insured'
  retentionPayment?: number; // Retention payment amount
  folder?: string; // Folder for uploaded files
}

export interface VendorData {
  uuid?: string;
  referenceId?: string;
  aliases?: {
    edges?: Array<{
      node?: {
        uuid?: string;
        name?: string;
      };
    }>;
  };
  services?: {
    edges?: Array<{
      node?: {
        uuid?: string;
        name?: string;
      };
    }>;
  };
}

interface AddPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PaymentFormData) => void;
  reserveType: string;
  reserveTypeUuid: string;
  reserveId: string;
  paymentId?: string; // Optional payment ID when editing
  isEditing?: boolean; // Whether we're editing an existing payment
  reservePayments?: ReservePayment[]; // Reserve payments from parent component
  remainingReserve?: number; // Remaining reserve amount from parent component
  vendors?: VendorData[]; // Vendors data passed from parent component (from claim query)
  remainingRetention?: number; // Remaining retention amount from reserves data
  totalReserveAmount?: number; // Total reserve amount from reserves data
}

interface AccordionSectionProps {
  title: string;
  children: React.ReactNode;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  children,
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Box>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mb: 1,
        }}
      >
        <Box className="claim-title">
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          {title}
        </Box>
      </Box>
      {expanded && (
        <Box className="payment-accordion-details-style">{children}</Box>
      )}
    </Box>
  );
};

export const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  open,
  onClose,
  onSubmit,
  reserveType = 'ALAE',
  reserveTypeUuid,
  reserveId,
  paymentId,
  isEditing = false,
  reservePayments = [],
  remainingReserve = 0,
  remainingRetention = 0,
  totalReserveAmount = 0,
}) => {
  // Get claim entity context
  const { claimEntity } = useOutletContext<IClaimDetailsProps>();
  const [originalPaymentAmount, setOriginalPaymentAmount] = useState<number>(0);
  const [dynamicRetention, setDynamicRetention] = useState<number>(0);
  const [originalReservePaymentAmount, setOriginalReservePaymentAmount] =
    useState<number>(0);
  const claimRemainingRetention = claimEntity?.remainingRetention ?? 0;
  const isUaleReserve = reserveType === 'ULAE';
  const isPrimaryPolicy =
    claimEntity.quote?.node?.policies?.edges[0].node.type === 'PRIMARY';

  const dynamicResolver = useMemo(
    () =>
      yupResolver(
        getPaymentSchema(
          reserveType,
          isEditing,
          originalPaymentAmount,
          claimRemainingRetention,
          isUaleReserve,
          dynamicRetention,
          isPrimaryPolicy
        )
      ),
    [
      reserveType,
      isPrimaryPolicy,
      isEditing,
      isUaleReserve,
      originalPaymentAmount,
      claimRemainingRetention,
      dynamicRetention,
    ]
  );

  const formMethods = useForm<PaymentFormData>({
    resolver: dynamicResolver,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      reserveAmount: 0,
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      paymentDate: new Date().toISOString().split('T')[0],
      type: ReservePaymentType.Ach,
      vendor: '',
      claimant: '',
      payee: '',
      careOf: '',
      mailingAddress: '',
      message: '', // Updated field name
      coverage: '',
      subCoverage: '',
      attachments: [], // Initialize empty array for attachments
      expenseType: '',
      lossType: '',
      subExpenseType: '',
      payeeType: reserveType === 'ULAE' ? 'vendor' : '', // Default to vendor for ULAE only
      retentionPayment: 0, // Default retention payment amount
      folder: UploadFolderName.ClaimInvoices, // Default folder for uploaded files
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitted },
    trigger,
    clearErrors,
  } = formMethods;

  // Explicitly type setValue, watch, and getValues to avoid deep instantiation errors
  const setValue = formMethods.setValue as any;
  const watch = formMethods.watch as any;
  const getValues = formMethods.getValues as any;
  const clearErrorsTyped = clearErrors as any;

  // Set up hooks before callbacks that use them
  const { DeleteAttachment } = DeleteNoteAttachment();
  const { notify } = useConvergeSnackBar();

  // State variables for file handling UI (must be before callbacks that use them)
  const [showAttachedFiles, setShowAttachedFiles] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState<IUploadFile[]>([]);
  const [showAttachments, setShowAttachments] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState(
    UploadFolderName.ClaimInvoices
  );
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    []
  );

  // Sync selectedFolder with form value
  useEffect(() => {
    const subscription = watch(
      (value: any, { name: fieldName }: { name?: string; type?: string }) => {
        if (fieldName === 'folder' && value.folder) {
          setSelectedFolder(value.folder as UploadFolderName);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, [watch]);

  // Memoized file handlers to avoid type instantiation issues

  // Handle removal of files - distinguish between new uploads and existing attachments
  const handleRemoveFile = useCallback(
    async (id: string) => {
      // Check if this is a newly uploaded file (has 'new-attachment' prefix)
      if (id && id.includes('new-attachment')) {
        // This is a newly uploaded file - just remove from local state
        const updated = uploadedFiles.filter((file) => file.id !== id);
        setUploadedFiles(updated);
        // Update form field to trigger validation
        const attachmentValue = updated.length > 0 ? updated : [];
        setValue('attachments', attachmentValue as any, {
          shouldValidate: true,
        });
        return;
      }

      // This is an existing attachment with a real UUID - call backend deletion
      try {
        const { errors } = await DeleteAttachment({
          variables: {
            attachmentUuid: id,
          },
        });

        if (errors) {
          notify('Failed to delete attachment', { variant: 'error' });
          return;
        }

        // Remove from local state on successful deletion
        const updatedAttachments = existingAttachments.filter(
          (attachment) => attachment.uuid !== id
        );
        setExistingAttachments(updatedAttachments);
        // Update form field to trigger validation
        const totalFiles = uploadedFiles.length + updatedAttachments.length;
        setValue(
          'attachments',
          totalFiles > 0 ? ([{}] as unknown as IUploadFile[]) : [],
          { shouldValidate: true }
        );
        notify('File has been deleted', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting attachment:', error);
        notify('Failed to delete attachment', { variant: 'error' });
      }
    },
    [uploadedFiles, setValue, DeleteAttachment, existingAttachments, notify]
  );

  function formatClaimKey(key: string) {
    if (key === 'CLAIM_FORMS') {
      return 'Claim Forms'; // or return "CLAIM_FORM" if you want it untouched
    }
    const cleaned = key.replace(/^CLAIM_/, '');

    // Replace underscores with spaces, lowercase all, then capitalize each word
    return cleaned
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  const handleFilesAdded = useCallback(
    (newFiles: IUploadFile[]) => {
      const updated = [...uploadedFiles, ...newFiles];
      setUploadedFiles(updated);
      // Update form field to trigger validation
      setValue('attachments', updated as unknown as IUploadFile[], {
        shouldValidate: true,
      });

      const intervals: NodeJS.Timeout[] = [];
      newFiles.forEach((file) => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 10;
          if (progress > 100) {
            clearInterval(interval);
          } else {
            setUploadedFiles((prev) =>
              prev.map((f) => (f.id === file.id ? { ...f, progress } : f))
            );
          }
        }, 300);
        intervals.push(interval);
      });
    },
    [setValue, uploadedFiles]
  );

  const handleAutoExpandAttachedFiles = useCallback(() => {
    if (!showAttachedFiles) {
      setShowAttachedFiles(true);
    }
  }, [showAttachedFiles]);

  // Set up the upsert payment mutation
  const { upsertReservePayment, loading: paymentLoading } =
    useUpsertReservePaymentMutation({});

  const hasRequiredAttachments = () => {
    return existingAttachments.length > 0 || uploadedFiles.length > 0;
  };

  const handleClose = () => {
    reset();
    setExistingAttachments([]);
    setUploadedFiles([]);
    setSelectedFolder(UploadFolderName.ClaimInvoices);
    onClose();
  };

  // Define the parameter type for useParams
  const { claimUuid } = useParams() as { claimUuid: string };
  const { vendors, contacts } = useSelectedVendorsAndContactListQuery(
    claimUuid || ''
  );

  const claimantsList = contacts.filter(
    (claimants) => claimants.type === ClaimantType.Claimant
  );

  // Watch the expense type to show/hide sub expense type
  const currentExpenseType = watch('expenseType') as string;

  // Watch the loss type for Loss payments
  const currentLossType = watch('lossType') as string;

  // Watch the sub expense type to show/hide coverage
  const currentSubExpenseType = watch('subExpenseType') as string;

  // Watch the payee type to show/hide vendor fields
  const currentPayeeType = watch('payeeType') as string;

  // Watch the coverage field to show sub-coverage options
  const currentCoverage = watch('coverage') as string;

  // Watch the amount field to calculate remaining reserves
  const currentReserveAmount = watch('reserveAmount') as number | string;

  // Watch the retention payment amount field
  const currentRetentionPayment = watch('retentionPayment') as number | string;

  // Fetch ALAE payment coverages for expense types, sub-expenses, and coverages
  const {
    data: alaePaymentCoveragesData,
    loading: alaePaymentCoveragesLoading,
  } = useAlaePaymentCoveragesQuery({
    filters: {
      claimUuid: claimUuid || '',
      reserveTypeUuid: reserveTypeUuid,
    },
    pageSize: 100, // Get all available options
    skip: reserveType !== 'ALAE',
  });

  // Set up close claim details query for refetching
  const { refetch: refetchCloseClaimDetails } = useCloseClaimDetailsQuery({
    claimUuid: claimUuid || '',
  });

  // Fetch Loss payment coverages for loss types and coverages
  const {
    data: lossPaymentCoveragesData,
    loading: lossPaymentCoveragesLoading,
  } = useLossPaymentCoveragesQuery({
    filters: {
      claimUuid: claimUuid || '',
      reserveTypeUuid: reserveTypeUuid,
    },
    pageSize: 100, // Get all available options
    skip: reserveType !== 'LOSS',
  });

  // Fetch ULAE payment coverages for expense types
  const {
    data: ulaePaymentCoveragesData,
    loading: ulaePaymentCoveragesLoading,
  } = useUlaePaymentCoveragesQuery({
    filters: {
      claimUuid: claimUuid || '',
      reserveTypeUuid: reserveTypeUuid,
    },
    pageSize: 100, // Get all available options
    skip: reserveType !== 'ULAE',
  });

  // Get claim UUID from URL parameters
  const handleFormSubmit = (data: PaymentFormData) => {
    if (!hasRequiredAttachments()) {
      notify(oneAttachmentRequired, { variant: 'error' });
      return;
    }

    // Prepare attachments - only send new files for now
    // The backend should handle preserving existing attachments in edit mode
    const attachments: Attachments | undefined =
      uploadedFiles && uploadedFiles.length > 0
        ? {
            files: uploadedFiles.map(
              (uploadFile): AttachmentUploadInput => ({
                file: uploadFile.file,
                folder: uploadFile.folder, // Use the selected folder from each file
              })
            ),
          }
        : undefined;

    // Prepare the payment input
    const paymentInput: ReservePaymentInput = {
      coverageUuid: data.coverage,
      subCoverageUuid: data.subCoverage ?? null,
      reserveAmount: Number(data.reserveAmount), // Use the payment amount as reserve amount
      retentionAmount: !isUaleReserve
        ? Number(data.retentionPayment) // Use retention payment amount
        : undefined,
      invoiceNumber: data.invoiceNumber,
      type: data.type,
      message: data.message, // Add message field
      vendorUuid: data.payeeType === 'vendor' ? data.vendor : null,
      insuredUuid:
        data.payeeType === 'insured'
          ? claimEntity.insuredEntity?.node.uuid
          : undefined,
      expenseTypeUuid: data.expenseType ?? null,
      subExpenseTypeUuid: data.subExpenseType ?? null,
      lossTypeUuid: data.lossType ?? null,
      claimantUuid: data.payeeType === 'claimant' ? data.claimant : null,
      attachments, // Add attachments
    };

    // When editing, include the payment UUID
    if (isEditing && paymentId) {
      paymentInput.paymentUuid = paymentId; // Add paymentUuid as well
    }

    // Call the mutation to create/update the payment
    const variables: {
      reserveUuid: string;
      reservePaymentInput: ReservePaymentInput;
    } = {
      reserveUuid: reserveId,
      reservePaymentInput: paymentInput,
    };

    // For edit operations, include paymentUuid in the top-level variables
    if (isEditing && paymentId) {
      variables.reservePaymentInput.paymentUuid = paymentId;
    }

    // Submit the payment mutation with the prepared variables

    upsertReservePayment({
      variables: variables,
    })
      .then((result) => {
        const graphqlErrors = result?.errors;
        if (graphqlErrors?.length) {
          notify(`Failed to save payment. ${graphqlErrors[0].message}`, {
            variant: 'error',
          });
          return;
        }
        onSubmit(data);
        // Refetch close claim details to update validation status
        refetchCloseClaimDetails();
        handleClose();
      })
      .catch((error) => {
        notify(
          `Failed to save payment. Please try again: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          { variant: 'error' }
        );
      });
  };

  const { data } = useGetFolderList();
  const folderList =
    data?.uploadFolders?.edges?.map((folder) => ({
      label: folder.node.label || folder.node.name,
      value: folder.node.name,
    })) || [];

  const dynamicRemainingReserve = useMemo(() => {
    const reserveAmount =
      typeof currentReserveAmount === 'string'
        ? parseFloat(currentReserveAmount.replace(/,/g, '')) || 0
        : currentReserveAmount || 0;

    if (
      isEditing &&
      originalReservePaymentAmount &&
      originalReservePaymentAmount === reserveAmount
    ) {
      return remainingReserve;
    } else if (isEditing && originalReservePaymentAmount !== reserveAmount) {
      return remainingReserve + originalReservePaymentAmount - reserveAmount;
    }

    return remainingReserve - reserveAmount;
  }, [
    currentReserveAmount,
    remainingReserve,
    originalReservePaymentAmount,
    isEditing,
  ]);

  // Calculate dynamic remaining retention amount using claimEntity data
  // Priority: claimEntity.remainingRetention > props.remainingRetention > 0
  const dynamicRemainingRetention = useMemo(() => {
    const numericRetentionPayment =
      typeof currentRetentionPayment === 'string'
        ? parseFloat(currentRetentionPayment.replace(/,/g, '')) || 0
        : currentRetentionPayment || 0;

    // Use remainingRetention from claimEntity if available, otherwise fall back to prop
    if (!reserveId) {
      return claimRemainingRetention - numericRetentionPayment;
    } else if (reserveId && originalPaymentAmount) {
      if (originalPaymentAmount === numericRetentionPayment) {
        return claimRemainingRetention;
      } else {
        return (
          claimRemainingRetention +
          originalPaymentAmount -
          numericRetentionPayment
        );
      }
    } else if (reserveId && !originalPaymentAmount) {
      return claimRemainingRetention - numericRetentionPayment;
    }
    setDynamicRetention(claimRemainingRetention - numericRetentionPayment);
    return claimRemainingRetention - numericRetentionPayment;
  }, [
    currentRetentionPayment,
    reserveId,
    originalPaymentAmount,
    claimRemainingRetention,
  ]);

  // Generate coverage options - use ALAE/Loss payment coverages based on reserve type
  const coverageOptions = useMemo(() => {
    // For ALAE payments with expense type selected
    if (
      reserveType === 'ALAE' &&
      currentExpenseType &&
      alaePaymentCoveragesData?.alaePaymentCoverages?.edges
    ) {
      // Find the selected expense type
      const selectedExpenseType =
        alaePaymentCoveragesData.alaePaymentCoverages.edges.find(
          (edge: any) => edge.node.uuid === currentExpenseType
        );

      if (selectedExpenseType?.node) {
        // Check if this expense type has sub-expenses
        const hasSubExpenses =
          selectedExpenseType.node.subExpenses &&
          selectedExpenseType.node.subExpenses.length > 0;

        if (hasSubExpenses && currentSubExpenseType) {
          // If has sub-expenses and one is selected, get coverages from sub-expense
          const selectedSubExpenseType =
            selectedExpenseType.node.subExpenses.find(
              (subExpense: any) => subExpense.uuid === currentSubExpenseType
            );

          if (selectedSubExpenseType?.coverages) {
            return selectedSubExpenseType.coverages.map((coverage: any) => ({
              value: coverage.uuid,
              label: coverage.name,
            }));
          }
        } else if (!hasSubExpenses && selectedExpenseType.node.coverages) {
          // If no sub-expenses, get coverages directly from expense type
          return selectedExpenseType.node.coverages.map((coverage: any) => ({
            value: coverage.uuid,
            label: coverage.name,
          }));
        }
      }
      return [];
    }

    // For Loss payments with loss type selected
    if (
      reserveType === 'LOSS' &&
      currentLossType &&
      lossPaymentCoveragesData?.lossPaymentCoverages?.edges
    ) {
      // Find the selected loss type
      const selectedLossType =
        lossPaymentCoveragesData.lossPaymentCoverages.edges.find(
          (edge: any) => edge.node.uuid === currentLossType
        );

      if (
        selectedLossType?.node.coverages &&
        selectedLossType.node.coverages.length > 0
      ) {
        return selectedLossType.node.coverages.map((coverage: any) => ({
          value: coverage.uuid,
          label: coverage.name,
        }));
      }
      // If loss type has no coverages, fall through to use reserve coverages
    }

    return [];
  }, [
    reserveType,
    currentExpenseType,
    currentLossType,
    currentSubExpenseType,
    alaePaymentCoveragesData,
    lossPaymentCoveragesData,
  ]);

  const vendorOptions = useMemo(() => {
    return (
      vendors?.map((vendor) => {
        const alias = vendor?.aliases?.edges?.[0]?.node?.name || '';
        return {
          value: vendor?.uuid || '',
          label: alias,
          services:
            vendor?.services?.edges?.map((serviceEdge) => ({
              value: serviceEdge?.node?.uuid || '',
              label: serviceEdge?.node?.name || '',
            })) || [],
        };
      }) || []
    );
  }, [vendors]);

  const claimantOptions = useMemo(() => {
    return (
      claimantsList?.map((claimant) => ({
        value: claimant?.uuid || '',
        label:
          `${claimant?.firstName || ''} ${claimant?.lastName || ''}`.trim() ||
          claimant?.companyName ||
          '',
      })) || []
    );
  }, [claimantsList]);

  // Check if selected expense type has sub-expenses
  const hasSubExpenses = useMemo(() => {
    if (
      reserveType !== 'ALAE' ||
      !currentExpenseType ||
      !alaePaymentCoveragesData?.alaePaymentCoverages?.edges
    ) {
      return false;
    }

    const selectedExpenseType =
      alaePaymentCoveragesData.alaePaymentCoverages.edges.find(
        (edge: any) => edge.node.uuid === currentExpenseType
      );

    return (
      selectedExpenseType?.node.subExpenses &&
      selectedExpenseType.node.subExpenses.length > 0
    );
  }, [reserveType, currentExpenseType, alaePaymentCoveragesData]);

  const hasAmountError =
    errors?.reserveAmount?.message === paymentExceedsReservesError ||
    (dynamicRemainingReserve !== undefined && dynamicRemainingReserve < 0);

  const hasRetentionError =
    errors?.retentionPayment?.message === retentionExceedsRemainingError ||
    (dynamicRemainingRetention !== undefined && dynamicRemainingRetention < 0);

  // Generate sub-coverage options based on selected coverage
  const subCoverageOptions = useMemo(() => {
    if (!currentCoverage) {
      return [];
    }

    // For ALAE payments, get sub-coverages from the selected coverage
    if (
      reserveType === 'ALAE' &&
      currentSubExpenseType &&
      alaePaymentCoveragesData?.alaePaymentCoverages?.edges
    ) {
      // Find the selected expense type
      const selectedExpenseType =
        alaePaymentCoveragesData.alaePaymentCoverages.edges.find(
          (edge: any) => edge.node.uuid === currentExpenseType
        );

      if (selectedExpenseType?.node.subExpenses) {
        // Find the selected sub-expense type
        const selectedSubExpenseType =
          selectedExpenseType.node.subExpenses.find(
            (subExpense: any) => subExpense.uuid === currentSubExpenseType
          );

        if (selectedSubExpenseType?.coverages) {
          // Find the selected coverage
          const selectedCoverage = selectedSubExpenseType?.coverages.find(
            (coverage: any) => coverage.uuid === currentCoverage
          );

          if (selectedCoverage?.subCoverages?.edges) {
            return selectedCoverage.subCoverages.edges.map((edge: any) => ({
              value: edge.node.uuid,
              label: edge.node.name,
            }));
          }
        }
      }
      return [];
    }

    // For LOSS payments, get sub-coverages from the selected coverage
    if (
      reserveType === 'LOSS' &&
      currentLossType &&
      lossPaymentCoveragesData?.lossPaymentCoverages?.edges
    ) {
      // Find the selected loss type
      const selectedLossType =
        lossPaymentCoveragesData.lossPaymentCoverages.edges.find(
          (edge: any) => edge.node.uuid === currentLossType
        );

      if (selectedLossType?.node.coverages) {
        // Find the selected coverage
        const selectedCoverage = selectedLossType.node.coverages.find(
          (coverage: any) => coverage.uuid === currentCoverage
        );

        if (selectedCoverage?.subCoverages?.edges) {
          return selectedCoverage.subCoverages.edges.map((edge: any) => ({
            value: edge.node.uuid,
            label: edge.node.name,
          }));
        }
      }
      return [];
    }

    return [];
  }, [
    currentCoverage,
    reserveType,
    currentExpenseType,
    currentSubExpenseType,
    currentLossType,
    alaePaymentCoveragesData,
    lossPaymentCoveragesData,
  ]);
  const normalizeFolder = (folder: string) =>
    folder.toUpperCase().replace(/\s+/g, '_');
  // Get expense type options from ALAE payment coverages query
  const expenseTypeOptions = useMemo(() => {
    if (
      !alaePaymentCoveragesData?.alaePaymentCoverages?.edges ||
      reserveType !== 'ALAE'
    ) {
      // Fallback to hardcoded options if query data not available
      return [
        { value: 'legal_fees', label: 'Legal Fees' },
        { value: 'expert_witness', label: 'Expert Witness' },
        { value: 'court_costs', label: 'Court Costs' },
        { value: 'investigation', label: 'Investigation' },
        { value: 'medical_records', label: 'Medical Records' },
        { value: 'surveillance', label: 'Surveillance' },
        { value: 'consulting', label: 'Consulting' },
        { value: 'other', label: 'Other' },
      ];
    }

    // Extract expense types from the query data (main level nodes)
    return alaePaymentCoveragesData.alaePaymentCoverages.edges.map(
      (edge: any) => ({
        value: edge.node.uuid,
        label: edge.node.name,
      })
    );
  }, [alaePaymentCoveragesData, reserveType]);

  // Get loss type options from Loss payment coverages query
  const lossTypeOptions = useMemo(() => {
    if (
      !lossPaymentCoveragesData?.lossPaymentCoverages?.edges ||
      reserveType !== 'LOSS'
    ) {
      // Fallback to hardcoded options if query data not available
      return [
        { value: 'property_damage', label: 'Property Damage' },
        { value: 'bodily_injury', label: 'Bodily Injury' },
        { value: 'personal_injury', label: 'Personal Injury' },
        { value: 'business_interruption', label: 'Business Interruption' },
        { value: 'cyber_loss', label: 'Cyber Loss' },
        { value: 'other_loss', label: 'Other Loss' },
      ];
    }

    // Extract loss types from the query data (main level nodes)
    return lossPaymentCoveragesData.lossPaymentCoverages.edges.map(
      (edge: any) => ({
        value: edge.node.uuid,
        label: edge.node.name,
      })
    );
  }, [lossPaymentCoveragesData, reserveType]);

  // Get expense type options from ULAE payment coverages query (for ULAE reserve type)
  const ulaeExpenseTypeOptions = useMemo(() => {
    if (
      !ulaePaymentCoveragesData?.ulaePaymentCoverages?.edges ||
      reserveType !== 'ULAE'
    ) {
      // Fallback to hardcoded options if query data not available
      return [
        { value: 'attorney_fees', label: 'Attorney Fees' },
        { value: 'court_costs', label: 'Court Costs' },
        { value: 'expert_fees', label: 'Expert Fees' },
        { value: 'investigation_costs', label: 'Investigation Costs' },
        { value: 'other_ulae', label: 'Other ULAE' },
      ];
    }

    // Extract expense types from the ULAE query data
    return ulaePaymentCoveragesData.ulaePaymentCoverages.edges.map(
      (edge: any) => ({
        value: edge.node.uuid,
        label: edge.node.name,
      })
    );
  }, [ulaePaymentCoveragesData, reserveType]);

  // Get sub expense type options based on selected expense type from query data
  const getSubExpenseTypeOptions = useCallback(
    (expenseType: string) => {
      if (
        !alaePaymentCoveragesData?.alaePaymentCoverages?.edges ||
        reserveType !== 'ALAE'
      ) {
        // Fallback to hardcoded options if query data not available
        const subExpenseMap: Record<
          string,
          Array<{ value: string; label: string }>
        > = {
          legal_fees: [
            { value: 'attorney_hourly', label: 'Attorney Hourly Fees' },
            { value: 'paralegal_fees', label: 'Paralegal Fees' },
            { value: 'legal_research', label: 'Legal Research' },
            { value: 'document_review', label: 'Document Review' },
            { value: 'court_appearances', label: 'Court Appearances' },
          ],
          expert_witness: [
            { value: 'medical_expert', label: 'Medical Expert' },
            { value: 'engineering_expert', label: 'Engineering Expert' },
            { value: 'financial_expert', label: 'Financial Expert' },
            {
              value: 'accident_reconstruction',
              label: 'Accident Reconstruction',
            },
            { value: 'vocational_expert', label: 'Vocational Expert' },
          ],
          court_costs: [
            { value: 'filing_fees', label: 'Filing Fees' },
            { value: 'service_fees', label: 'Service of Process Fees' },
            { value: 'court_reporter', label: 'Court Reporter Fees' },
            { value: 'transcript_costs', label: 'Transcript Costs' },
            { value: 'jury_fees', label: 'Jury Fees' },
          ],
          investigation: [
            { value: 'private_investigator', label: 'Private Investigator' },
            { value: 'scene_investigation', label: 'Scene Investigation' },
            { value: 'witness_interviews', label: 'Witness Interviews' },
            { value: 'background_checks', label: 'Background Checks' },
            { value: 'asset_search', label: 'Asset Search' },
          ],
          medical_records: [
            { value: 'hospital_records', label: 'Hospital Records' },
            { value: 'physician_records', label: 'Physician Records' },
            { value: 'therapy_records', label: 'Therapy Records' },
            { value: 'imaging_records', label: 'Imaging Records' },
            { value: 'lab_results', label: 'Lab Results' },
          ],
          surveillance: [
            { value: 'video_surveillance', label: 'Video Surveillance' },
            { value: 'activity_checks', label: 'Activity Checks' },
            {
              value: 'social_media_monitoring',
              label: 'Social Media Monitoring',
            },
            {
              value: 'background_surveillance',
              label: 'Background Surveillance',
            },
          ],
          consulting: [
            { value: 'claims_consulting', label: 'Claims Consulting' },
            { value: 'risk_assessment', label: 'Risk Assessment' },
            { value: 'case_strategy', label: 'Case Strategy' },
            { value: 'settlement_analysis', label: 'Settlement Analysis' },
          ],
          other: [
            { value: 'travel_expenses', label: 'Travel Expenses' },
            { value: 'administrative_costs', label: 'Administrative Costs' },
            { value: 'technology_costs', label: 'Technology Costs' },
            { value: 'miscellaneous', label: 'Miscellaneous' },
          ],
        };
        return subExpenseMap[expenseType] || [];
      }
      // Find the selected expense type and return its sub-expenses
      const selectedExpenseType =
        alaePaymentCoveragesData.alaePaymentCoverages.edges.find(
          (edge: any) => edge.node.uuid === expenseType
        );

      if (!selectedExpenseType?.node.subExpenses) {
        return [];
      }

      return selectedExpenseType.node.subExpenses.map((subExpense: any) => ({
        value: subExpense.uuid,
        label: subExpense.name,
      }));
    },
    [alaePaymentCoveragesData, reserveType]
  );

  // Note: File attachments are now managed through form state via FileUploader component

  // Reset form when modal opens in create mode
  useEffect(() => {
    if (open && !isEditing) {
      // Reset to default values for create mode
      reset({
        reserveAmount: 0,
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        paymentDate: new Date().toISOString().split('T')[0],
        type: ReservePaymentType.Ach,
        vendor: '',
        claimant: '',
        payee: '',
        careOf: '',
        mailingAddress: '',
        message: '',
        coverage: '',
        subCoverage: '',
        attachments: [],
        expenseType: '',
        lossType: '',
        subExpenseType: '',
        payeeType: reserveType === 'ULAE' ? 'vendor' : '',
        retentionPayment: 0,
        folder: UploadFolderName.ClaimInvoices,
      });
      // Clear existing attachments
      setExistingAttachments([]);
      // Clear uploaded files
      setUploadedFiles([]);
      // Reset selected folder
      setSelectedFolder(UploadFolderName.ClaimInvoices);
      // Clear service options
      // Reset original payment amount
      setOriginalPaymentAmount(0);
    }
  }, [open, isEditing, reset, reserveType]);

  useEffect(() => {
    const payment = reservePayments.find((p) => p.uuid === paymentId);
    const expenseType = (payment as any)?.expenseType?.uuid;
    if (currentExpenseType && currentExpenseType !== expenseType) {
      setValue('coverage', '');
      setValue('subCoverage', '');
      setValue('subExpenseType', '');
    }
  }, [currentExpenseType, formMethods, paymentId, reservePayments, setValue]);

  // Ensure ACH payment method is always selected by default
  useEffect(() => {
    // Set payment method to ACH if not already set
    if (!getValues('type')) {
      setValue('type', ReservePaymentType.Ach, { shouldValidate: true });
    }
  }, [currentReserveAmount, formMethods, getValues, isEditing, setValue]);

  // Populate form with existing payment data when editing
  useEffect(() => {
    if (
      isEditing &&
      paymentId &&
      reservePayments &&
      reservePayments.length > 0
    ) {
      // Find the payment to edit in the reservePayments array
      const payment = reservePayments.find((p) => p.uuid === paymentId) as any;

      if (payment) {
        setOriginalPaymentAmount(payment.retentionAmount || 0);
        setOriginalReservePaymentAmount(payment.reserveAmount || 0);
        // Found the payment to edit

        // Reset form with existing payment data
        // Extract values directly from the reservePayments array
        const formValues = {
          // Basic fields
          reserveAmount: payment.reserveAmount || 0,
          invoiceNumber: payment.invoiceNumber || '',
          invoiceDate:
            payment.invoiceDate || new Date().toISOString().split('T')[0],
          paymentDate:
            payment.paymentDate || new Date().toISOString().split('T')[0],
          type: (payment.type as ReservePaymentType) || ReservePaymentType.Ach,

          // For payee, read directly from reservePayment
          payee: payment.payee || '',

          // For care of field
          careOf: payment.careOf || '',

          // For message field
          message: payment.message || '',

          // For mailing address, read from payeeAddress
          mailingAddress: payment.payeeAddress || '',

          // For coverage UUID - get from expense type or sub expense type for ALAE
          coverage: (() => {
            // For ALAE payments, try to get coverage from expense type structure
            if (reserveType === 'ALAE' && payment.expenseType) {
              // If there's a sub expense type, get coverage from it
              if (payment.expenseType.subExpenseType?.coverage?.uuid) {
                return payment.expenseType.subExpenseType?.coverage?.uuid || '';
              }
              // Otherwise get coverage from main expense type
              if (payment.expenseType.coverage?.uuid) {
                return payment.expenseType.coverage.uuid || '';
              }
            }

            // For LOSS payments, get coverage from loss type structure
            if (reserveType === 'LOSS' && payment.lossType?.coverage?.uuid) {
              return payment.lossType.coverage?.uuid || '';
            }

            return '';
          })(),

          // For sub coverage UUID
          subCoverage:
            payment.expenseType?.subExpenseType?.coverage?.subCoverage?.uuid ||
            payment.expenseType?.coverage?.subCoverage?.uuid ||
            payment.lossType?.coverage?.subCoverage?.uuid ||
            '',

          // For vendor UUID
          vendor: payment.vendor?.node?.uuid || '',
          // typeof payment.vendor === 'object'
          //   ? payment.vendor?.uuid || ''
          //   : payment.vendorUuid || '',

          // For claimant UUID
          claimant: payment.claimant?.node?.uuid || '',

          expenseType: payment.expenseType?.uuid || payment.expenseType || '',

          lossType: payment.lossType?.uuid || payment.lossType || '',

          // For sub expense type
          subExpenseType: payment.expenseType?.uuid
            ? payment.expenseType?.subExpenseType?.uuid
            : '',

          payeeType: payment.vendor?.node?.uuid
            ? 'vendor'
            : payment.claimant?.node?.uuid
            ? 'claimant'
            : !isEditing
            ? 'vendor'
            : 'insured',

          // Add retention payment fields from existing payment data
          retentionPayment: payment.retentionAmount || 0,

          // Set folder from first attachment if available, otherwise default
          folder:
            payment.attachments?.edges?.[0]?.node?.folder ||
            UploadFolderName.ClaimInvoices,
        };

        // Apply extracted values to the form
        reset(formValues);

        // Extract existing attachments
        if (payment.attachments?.edges?.length) {
          const attachments = payment.attachments.edges
            .filter((edge: any) => Boolean(edge?.node))
            .map((edge: any) => edge.node);
          setExistingAttachments(attachments);
          // Update form field so validation passes
          setValue('attachments', attachments as unknown as IUploadFile[], {
            shouldValidate: true,
          });

          // Set folder state from first attachment if available
          if (attachments[0]?.folder) {
            setSelectedFolder(attachments[0].folder as UploadFolderName);
          }
        } else {
          setExistingAttachments([]);
          setValue('attachments', [], { shouldValidate: true });
        }
      } else {
        // Clear existing attachments if no payment found
        setExistingAttachments([]);
      }
    } else {
      // Clear existing attachments when not editing
      setExistingAttachments([]);
    }
  }, [
    isEditing,
    paymentId,
    reservePayments,
    reset,
    vendorOptions,
    reserveType,
    setValue,
  ]);

  const RemainingReservesBlock = (
    <>
      <Typography className="form-label">REMAINING</Typography>
      <Typography
        sx={{
          color:
            hasAmountError || dynamicRemainingReserve < 0
              ? '#D32F2F !important'
              : 'inherit',
        }}
        variant="body2"
        className="form-value"
      >
        {new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(dynamicRemainingReserve)}
      </Typography>
    </>
  );

  const paymentTypeOptions = [
    { value: ReservePaymentType.Ach, label: 'ACH' },
    {
      value: ReservePaymentType.Check,
      label: 'Check',
    },
    {
      value: ReservePaymentType.WireTransfer,
      label: 'Wire Transfer',
    },
  ];

  const isReservePaymentDisabled =
    reserveType !== 'ULAE' && dynamicRemainingRetention > 0 && isPrimaryPolicy;
  const onReserveBlur = () => {
    if (currentReserveAmount !== 0 && !currentReserveAmount) {
      setValue('reserveAmount', 0);
    }
  };

  const onRetentionBlur = () => {
    if (currentRetentionPayment !== 0 && !currentRetentionPayment) {
      setValue('retentionPayment', 0);
    }
  };

  const PayeeTypeLabel = () => (
    <Typography
      className="form-label"
      sx={{
        color: errors.payeeType ? '#D32F2F' : 'inherit',
      }}
    >
      {'PAYEE TYPE'}
    </Typography>
  );

  const PayeeTypeMessage = () => (
    <Typography variant="caption" className="payeeType-message">
      {errors?.payeeType?.message || ''}
    </Typography>
  );
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      className="payment-modal-wrapper"
    >
      <Box className="modal-header">
        <Typography variant="h2" className="modal-title">
          {isEditing ? 'Edit Payment' : 'Add Payment'} For{' '}
          {reserveType === 'LOSS' ? 'Loss' : reserveType}
        </Typography>
        <CloseIcon className="close-icon" onClick={handleClose} />
      </Box>
      <DialogContent>
        <FormProvider {...formMethods}>
          <Box component="form" className="form-container">
            <Grid container spacing={1}>
              {/* Left Column - Payee Details */}
              <Grid item xs={6}>
                {/* Section 1: Payee Details */}
                <AccordionSection title="Payee Details">
                  <Grid container spacing={4}>
                    {/* Payee Type Radio Buttons (only for ALAE and ULAE) */}
                    {reserveType === 'ALAE' && (
                      <Grid item xs={10} sx={{ mb: 2 }}>
                        <PayeeTypeLabel />
                        <Controller
                          name="payeeType"
                          control={control as any}
                          render={({ field }) => (
                            <RadioGroup
                              {...field}
                              row
                              className="radio-group-horizontal"
                              onChange={(e) => {
                                const newPayeeType = e.target.value;
                                field.onChange(e);

                                // Clear vendor/invoice fields when switching away from vendor
                                if (newPayeeType !== 'vendor') {
                                  setValue('vendor', '', {
                                    shouldValidate: false,
                                  });
                                  setValue('invoiceNumber', '', {
                                    shouldValidate: false,
                                  });
                                  clearErrorsTyped('vendor');
                                  clearErrorsTyped('invoiceNumber');
                                }
                                // Clear claimant field when switching away from claimant
                                if (newPayeeType !== 'claimant') {
                                  setValue('claimant', '', {
                                    shouldValidate: false,
                                  });
                                  clearErrorsTyped('claimant');
                                }
                                // Trigger validation after state updates
                                if (isSubmitted) {
                                  setTimeout(() => {
                                    trigger();
                                  }, 50);
                                }
                              }}
                            >
                              <FormControlLabel
                                value="vendor"
                                control={
                                  <Radio
                                    className="radio-button unchecked"
                                    classes={{ checked: 'checked' }}
                                  />
                                }
                                label="Vendor"
                                sx={{ width: '16%' }}
                              />
                              <FormControlLabel
                                value="insured"
                                control={
                                  <Radio
                                    className="radio-button unchecked"
                                    classes={{ checked: 'checked' }}
                                  />
                                }
                                label="Insured"
                                sx={{ width: '16%' }}
                              />
                            </RadioGroup>
                          )}
                        />
                        {errors.payeeType && <PayeeTypeMessage />}
                      </Grid>
                    )}

                    {/* Vendor and Invoice Number (for ALAE - shown only when no selection or vendor selected) */}
                    {reserveType === 'ALAE' &&
                      (currentPayeeType === 'vendor' || !currentPayeeType) && (
                        <>
                          <Grid
                            item
                            xs={5}
                            sx={{ paddingTop: '12px !important' }}
                          >
                            <Controller
                              name="vendor"
                              control={control as any}
                              render={({ field: { ref, ...field } }) => (
                                <NewTypeAhead
                                  {...field}
                                  disabled={currentPayeeType !== 'vendor'}
                                  label="VENDOR"
                                  placeholder="Select Vendor"
                                  options={vendorOptions}
                                  containerWidth={12}
                                  onChange={(value) => {
                                    field.onChange(value);
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid
                            item
                            sx={{ paddingTop: '12px !important' }}
                            xs={5}
                          >
                            <Controller
                              name="invoiceNumber"
                              control={control as any}
                              render={({ field: { ref, ...field } }) => (
                                <NewFormTextField
                                  {...field}
                                  label="INVOICE NUMBER"
                                  placeholder="Enter Invoice Number"
                                  containerWidth={12}
                                  disabled={currentPayeeType !== 'vendor'}
                                />
                              )}
                            />
                          </Grid>
                        </>
                      )}

                    {/* Payee Type Radio Buttons (only for LOSS) */}
                    {reserveType === 'LOSS' && (
                      <Grid item xs={10} sx={{ mb: 2 }}>
                        <PayeeTypeLabel />
                        <Controller
                          name="payeeType"
                          control={control as any}
                          render={({ field }) => (
                            <RadioGroup
                              {...field}
                              row
                              className="radio-group-horizontal"
                              onChange={(e) => {
                                const newPayeeType = e.target.value;
                                field.onChange(e);

                                // Clear vendor/invoice fields when switching away from vendor
                                if (newPayeeType !== 'vendor') {
                                  setValue('vendor', '', {
                                    shouldValidate: false,
                                  });
                                  setValue('invoiceNumber', '', {
                                    shouldValidate: false,
                                  });
                                  clearErrorsTyped('vendor');
                                  clearErrorsTyped('invoiceNumber');
                                }
                                // Clear claimant field when switching away from claimant
                                if (newPayeeType !== 'claimant') {
                                  setValue('claimant', '', {
                                    shouldValidate: false,
                                  });
                                  clearErrorsTyped('claimant');
                                }
                                // Trigger validation after state updates
                                if (isSubmitted) {
                                  setTimeout(() => {
                                    trigger();
                                  }, 50);
                                }
                              }}
                            >
                              <FormControlLabel
                                value="vendor"
                                control={
                                  <Radio
                                    className="radio-button unchecked"
                                    classes={{ checked: 'checked' }}
                                  />
                                }
                                label="Vendor"
                                sx={{ width: '16%' }}
                              />
                              <FormControlLabel
                                value="insured"
                                control={
                                  <Radio
                                    className="radio-button unchecked"
                                    classes={{ checked: 'checked' }}
                                  />
                                }
                                label="Insured"
                                sx={{ width: '16%' }}
                              />
                              <FormControlLabel
                                value="claimant"
                                control={
                                  <Radio
                                    className="radio-button unchecked"
                                    classes={{ checked: 'checked' }}
                                  />
                                }
                                label="Claimant"
                                sx={{ width: '16%' }}
                              />
                            </RadioGroup>
                          )}
                        />
                        {errors.payeeType && <PayeeTypeMessage />}
                      </Grid>
                    )}

                    {/* Vendor and Invoice Number (for LOSS - shown only when no selection or vendor selected) */}
                    {reserveType === 'LOSS' &&
                      (currentPayeeType === 'vendor' || !currentPayeeType) && (
                        <>
                          <Grid item xs={5} className="text-wrapper">
                            <Controller
                              name="vendor"
                              control={control as any}
                              render={({ field: { ref, ...field } }) => (
                                <NewTypeAhead
                                  {...field}
                                  disabled={currentPayeeType !== 'vendor'}
                                  label="VENDOR"
                                  placeholder="Select Vendor"
                                  options={vendorOptions}
                                  containerWidth={12}
                                  onChange={(value) => {
                                    field.onChange(value);
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={5} className="text-wrapper">
                            <Controller
                              name="invoiceNumber"
                              control={control as any}
                              render={({ field: { ref, ...field } }) => (
                                <NewFormTextField
                                  {...field}
                                  label="INVOICE NUMBER"
                                  placeholder="Enter Invoice Number"
                                  containerWidth={12}
                                  disabled={currentPayeeType !== 'vendor'}
                                />
                              )}
                            />
                          </Grid>
                        </>
                      )}

                    {/* Claimant dropdown (for LOSS - shown only when claimant is selected) */}
                    {reserveType === 'LOSS' &&
                      currentPayeeType === 'claimant' && (
                        <>
                          <Box
                            sx={{ margin: '24px 0 32px 32px', width: '90%' }}
                          >
                            <Controller
                              name="claimant"
                              control={control as any}
                              render={({ field: { ref, ...field } }) => (
                                <NewTypeAhead
                                  {...field}
                                  label="CLAIMANT"
                                  placeholder="Select Claimant"
                                  options={claimantOptions}
                                  containerWidth={20}
                                  onChange={(value) => {
                                    field.onChange(value);
                                  }}
                                />
                              )}
                            />
                          </Box>
                        </>
                      )}

                    {/* Payee Type - Vendor only for ULAE */}
                    {reserveType === 'ULAE' && (
                      <Grid item xs={10} sx={{ mb: 2 }}>
                        <Typography className="form-label">
                          PAYEE TYPE
                        </Typography>
                        <Controller
                          name="payeeType"
                          control={control as any}
                          defaultValue="vendor"
                          render={({ field }) => (
                            <RadioGroup
                              {...field}
                              row
                              className="radio-group-horizontal"
                              onChange={(e) => {
                                field.onChange(e);
                              }}
                            >
                              <FormControlLabel
                                value="vendor"
                                control={
                                  <Radio
                                    className="radio-button unchecked"
                                    classes={{ checked: 'checked' }}
                                  />
                                }
                                label="Vendor"
                                sx={{ width: '25%' }}
                              />
                            </RadioGroup>
                          )}
                        />
                      </Grid>
                    )}

                    {/* Vendor and Invoice Number (for ULAE - always shown since vendor is pre-selected) */}
                    {reserveType === 'ULAE' && (
                      <>
                        <Grid
                          item
                          xs={5}
                          sx={{ paddingTop: '18px !important' }}
                        >
                          <Controller
                            name="vendor"
                            control={control as any}
                            render={({ field: { ref, ...field } }) => (
                              <NewTypeAhead
                                {...field}
                                label="VENDOR"
                                placeholder="Select Vendor"
                                options={vendorOptions}
                                containerWidth={12}
                                onChange={(value) => {
                                  field.onChange(value);
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={5}
                          sx={{ paddingTop: '18px !important' }}
                        >
                          <Controller
                            name="invoiceNumber"
                            control={control as any}
                            render={({
                              field: { ref, ...field },
                              fieldState,
                            }) => (
                              <NewFormTextField
                                {...field}
                                label="INVOICE NUMBER"
                                placeholder="Enter Invoice Number"
                                containerWidth={12}
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </AccordionSection>

                {/* Section 2: Payment Details */}
                <Box className="payment-details-wrapper">
                  <AccordionSection title="Payment Details">
                    <Grid container spacing={4}>
                      {/* First row - Retention Payment, Payment Method, Remaining Retention - Hide for ULAE */}
                      {(reserveType === 'ALAE' || reserveType === 'LOSS') &&
                        isPrimaryPolicy && (
                          <>
                            <Grid item xs={4}>
                              <Box>
                                <FormNumberInputAdornmentField
                                  name="retentionPayment"
                                  label="RETENTION PAYMENT"
                                  disabled={
                                    (dynamicRemainingRetention === 0 &&
                                      !currentRetentionPayment &&
                                      !isEditing) ||
                                    (isEditing &&
                                      dynamicRemainingRetention === 0 &&
                                      !currentRetentionPayment)
                                  }
                                  adornmentText="$"
                                  thousandSeparator=","
                                  isAllowed={(values) =>
                                    checkPositiveNumber(values)
                                  }
                                  placeholder="0"
                                  containerWidth={12}
                                  onBlur={onRetentionBlur}
                                />
                                {hasRetentionError &&
                                  !errors?.retentionPayment && (
                                    <Typography
                                      variant="caption"
                                      className="retention-error-message"
                                    >
                                      {retentionExceedsRemainingError}
                                    </Typography>
                                  )}
                              </Box>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography className="form-label">
                                PAYMENT METHOD
                              </Typography>
                              <Typography
                                variant="h4"
                                sx={{ fontSize: '16px' }}
                              >
                                N/A
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography className="form-label">
                                REMAINING
                              </Typography>
                              <Typography
                                className="form-value"
                                variant="body2"
                                sx={{
                                  color:
                                    dynamicRemainingRetention < 0
                                      ? '#D32F2F !important'
                                      : 'inherit',
                                }}
                              >
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                }).format(dynamicRemainingRetention)}
                              </Typography>
                            </Grid>
                          </>
                        )}

                      {/* Second row - Reserve Payment, Payment Method, Remaining Reserve */}
                      <Grid item xs={4}>
                        <FormNumberInputAdornmentField
                          name="reserveAmount"
                          label="RESERVE PAYMENT"
                          disabled={isReservePaymentDisabled}
                          adornmentText="$"
                          thousandSeparator=","
                          isAllowed={(values) => checkPositiveNumber(values)}
                          placeholder="0"
                          containerWidth={12}
                          onBlur={onReserveBlur}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Controller
                          name="type"
                          control={control as any}
                          render={({
                            field: { ref, ...field },
                            fieldState,
                          }) => (
                            <NewFromSelectField
                              {...field}
                              name="Payment method"
                              label="PAYMENT METHOD"
                              placeholder="Select Payment Method"
                              disabled={isReservePaymentDisabled}
                              options={paymentTypeOptions}
                              containerWidth={12}
                              onChange={(value) => {
                                field.onChange(value);
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        {RemainingReservesBlock}
                      </Grid>

                      {/* Message to Finance - moved here from Payee Details */}
                      <Grid item xs={11.5} sx={{ width: '90% !important' }}>
                        <Controller
                          name="message"
                          control={control as any}
                          render={({ field: { ref, ...field } }) => (
                            <NewFormTextField
                              {...field}
                              label="MESSAGE TO FINANCE"
                              multiline
                              rows={2}
                              containerWidth={12}
                              placeholder="Enter Message to Finance if Necessary"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </AccordionSection>
                </Box>
              </Grid>

              {/* Right Column - Expense Details and Attachments */}
              <Grid item xs={6}>
                {/* Section 2: Expense Details */}
                <Box sx={{ padding: '0px 0px 32px 0px' }}>
                  <AccordionSection
                    title={`${
                      reserveType === 'LOSS' ? 'Loss' : 'Expense'
                    } Details`}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '68px',
                      }}
                    >
                      {/* Expense Type - only for ALAE */}
                      {reserveType === 'ALAE' && (
                        <Box>
                          <Controller
                            name="expenseType"
                            control={control as any}
                            render={({
                              field: { ref, ...field },
                              fieldState,
                            }) => (
                              <NewTypeAhead
                                {...field}
                                label="EXPENSE TYPE"
                                placeholder="Select Expense Type"
                                options={expenseTypeOptions}
                                containerWidth={12}
                                isLoading={alaePaymentCoveragesLoading}
                                onChange={(value) => {
                                  field.onChange(value);
                                }}
                              />
                            )}
                          />
                        </Box>
                      )}

                      {/* Loss Type - only for LOSS */}
                      {reserveType === 'LOSS' && (
                        <Box>
                          <Controller
                            name="lossType"
                            control={control as any}
                            render={({
                              field: { ref, ...field },
                              fieldState,
                            }) => (
                              <NewTypeAhead
                                {...field}
                                label="LOSS TYPE"
                                placeholder="Select Loss Type"
                                options={lossTypeOptions}
                                isLoading={lossPaymentCoveragesLoading}
                                containerWidth={12}
                                onChange={(value) => {
                                  field.onChange(value);
                                }}
                              />
                            )}
                          />
                        </Box>
                      )}

                      {/* Sub Expense Type - only for ALAE when expense type is selected and has sub-expenses */}
                      {reserveType === 'ALAE' &&
                        currentExpenseType &&
                        hasSubExpenses && (
                          <Box>
                            <Controller
                              name="subExpenseType"
                              control={control as any}
                              render={({
                                field: { ref, ...field },
                                fieldState,
                              }) => (
                                <NewTypeAhead
                                  {...field}
                                  label="SUB EXPENSE TYPE"
                                  placeholder="Select Sub Expense Type"
                                  options={getSubExpenseTypeOptions(
                                    currentExpenseType
                                  )}
                                  containerWidth={12}
                                  onChange={(value) => {
                                    field.onChange(value);
                                  }}
                                />
                              )}
                            />
                          </Box>
                        )}

                      {/* Expense Type - only for ULAE */}
                      {reserveType === 'ULAE' && (
                        <Box>
                          <Controller
                            name="expenseType"
                            control={control as any}
                            render={({
                              field: { ref, ...field },
                              fieldState,
                            }) => (
                              <NewTypeAhead
                                {...field}
                                label="EXPENSE TYPE"
                                placeholder="Select Expense Type"
                                options={ulaeExpenseTypeOptions}
                                isLoading={ulaePaymentCoveragesLoading}
                                containerWidth={12}
                                onChange={(value) => {
                                  field.onChange(value);
                                }}
                              />
                            )}
                          />
                        </Box>
                      )}

                      {/* Coverage - show after subExpenseType for ALAE with sub-expenses, or directly after expenseType for ALAE without sub-expenses, or after lossType for Loss, or immediately for other types */}
                      {((reserveType === 'ALAE' &&
                        hasSubExpenses &&
                        currentSubExpenseType) ||
                        (reserveType === 'ALAE' &&
                          !hasSubExpenses &&
                          currentExpenseType) ||
                        (reserveType === 'LOSS' && currentLossType) ||
                        (reserveType !== 'ULAE' &&
                          reserveType !== 'ALAE' &&
                          reserveType !== 'LOSS')) && (
                        <Box>
                          <Controller
                            name="coverage"
                            control={control as any}
                            render={({
                              field: { ref, ...field },
                              fieldState,
                            }) => (
                              <NewTypeAhead
                                {...field}
                                className="coverage"
                                label="COVERAGE OR ENDORSEMENT"
                                placeholder="Select Coverage"
                                options={coverageOptions}
                                isLoading={lossPaymentCoveragesLoading}
                                containerWidth={12}
                                onChange={(value) => {
                                  field.onChange(value);
                                }}
                              />
                            )}
                          />
                        </Box>
                      )}

                      {/* Sub Coverage - show when coverage is selected and has sub-coverages */}
                      {currentCoverage && subCoverageOptions.length > 0 && (
                        <Box>
                          <Controller
                            name="subCoverage"
                            control={control as any}
                            render={({ field: { ref, ...field } }) => (
                              <NewTypeAhead
                                {...field}
                                label="SUB COVERAGE OR CHILD ENDORSEMENT (IF APPLICABLE)"
                                placeholder="Select Sub Coverage"
                                options={subCoverageOptions}
                                containerWidth={12}
                                onChange={(value) => {
                                  field.onChange(value);
                                }}
                              />
                            )}
                          />
                        </Box>
                      )}
                    </Box>
                  </AccordionSection>
                </Box>

                {/* Section 3: Attachments */}
                <Box className="vertical-spacing"></Box>

                {/* Add Attachments Section */}
                <Box sx={{ margin: '30px 0px 10px 0px' }}>
                  <Box
                    onClick={() => setShowAttachments(!showAttachments)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      mb: 1,
                    }}
                  >
                    <Box className="claim-title">
                      {showAttachments ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                      ADD ATTACHMENTS
                    </Box>
                  </Box>

                  {showAttachments && (
                    <>
                      <Box
                        sx={{ margin: '0px 120px 20px 24px' }}
                        className="claim-description"
                      >
                        All payments require supporting document(s) to be
                        uploaded.
                        <br />
                        <span style={{ fontStyle: 'italic' }}>
                          For cyber extortion, you must include the OFAC letter.
                        </span>
                      </Box>

                      <Box
                        sx={{ margin: '0px 0px 60px 0px' }}
                        className="claim-description"
                      >
                        <NewFromSelectField
                          name="folder"
                          label="UPLOAD FOLDER"
                          containerWidth={12}
                          options={folderList || []}
                          onChangeOption={(e) =>
                            setSelectedFolder(
                              (e?.value as UploadFolderName) ||
                                UploadFolderName.ClaimInvoices
                            )
                          }
                          placeholder="Select Folder"
                        />
                      </Box>
                      <Box className="claim-description claim-description-message"></Box>
                      <Box sx={{ margin: '0px 20px 20px 24px' }}>
                        <FileUploadDropzone
                          selectedFolder={selectedFolder}
                          disabled={selectedFolder.length === 0}
                          onFilesAdded={handleFilesAdded}
                          onAutoExpand={handleAutoExpandAttachedFiles}
                          error={!!errors.attachments}
                          existingFiles={[
                            ...existingAttachments.map((a) => ({
                              name: a.filePath?.split('/').pop() || '',
                              folder: normalizeFolder(
                                `CLAIM_${a.folder}` || ''
                              ),
                            })),
                            ...uploadedFiles.map((f) => ({
                              name: f.file.name,
                              folder: normalizeFolder(`${f.folder}`),
                            })),
                          ]}
                        />
                        {errors.attachments && (
                          <FormHelperText error sx={{ mt: 1, ml: 0 }}>
                            Document upload is required
                          </FormHelperText>
                        )}
                        <Box className="ofac-message-wrapper">
                          {showAttachedFiles && (
                            <Box sx={{ p: 2, pl: 0 }}>
                              <Box>
                                {/* Show existing attachments */}
                                {existingAttachments.map((attachment) => (
                                  <Box
                                    key={`existing-${attachment.uuid}`}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      mb: 1,
                                      py: 1,
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleRemoveFile(attachment.uuid || '');
                                      }}
                                      sx={{
                                        display: 'inline-flex',
                                        cursor: 'pointer',
                                        marginRight: '4px',
                                        '&:hover': {
                                          opacity: 0.7,
                                        },
                                      }}
                                    >
                                      <DeleteOutlineOutlinedIcon
                                        sx={{
                                          color: '#000',
                                          pointerEvents: 'none',
                                        }}
                                      />
                                    </Box>
                                    <Box className="upload-name">
                                      {attachment.url ? (
                                        <a
                                          href={attachment.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: 'inherit',
                                            textDecoration: 'none',
                                            cursor: 'pointer',
                                          }}
                                          onMouseEnter={(e) =>
                                            (e.currentTarget.style.textDecoration =
                                              'underline')
                                          }
                                          onMouseLeave={(e) =>
                                            (e.currentTarget.style.textDecoration =
                                              'none')
                                          }
                                        >
                                          {`${formatClaimKey(
                                            attachment.folder || 'Unknown'
                                          )} / ${
                                            attachment.filePath
                                              ?.split('/')
                                              .pop() ||
                                            attachment.uuid?.slice(0, 8) ||
                                            'attachment'
                                          }`}
                                        </a>
                                      ) : (
                                        `${formatClaimKey(
                                          attachment.folder || 'Unknown'
                                        )} / ${
                                          attachment.filePath
                                            ?.split('/')
                                            .pop() ||
                                          attachment.uuid?.slice(0, 8) ||
                                          'attachment'
                                        }`
                                      )}
                                    </Box>
                                  </Box>
                                ))}

                                {/* Show newly uploaded files */}
                                {uploadedFiles.map((uploadedFile) => (
                                  <Box
                                    key={uploadedFile.id}
                                    className="uploaded-file-item"
                                  >
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleRemoveFile(uploadedFile.id);
                                      }}
                                      sx={{
                                        display: 'inline-flex',
                                        cursor: 'pointer',
                                        marginRight: '4px',
                                        '&:hover': {
                                          opacity: 0.7,
                                        },
                                      }}
                                    >
                                      <DeleteOutlineOutlinedIcon
                                        sx={{
                                          color: '#000',
                                          pointerEvents: 'none',
                                        }}
                                      />
                                    </Box>
                                    <Box className="upload-name">
                                      {`${formatClaimKey(
                                        uploadedFile.folder
                                      )} / ${uploadedFile.file.name}`}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>

              {/* Hidden date fields for backend compatibility */}
              <Box sx={{ display: 'none', mt: 3 }}>
                <Controller
                  name="invoiceDate"
                  control={control as any}
                  render={({ field }) => <TextField {...field} type="date" />}
                />
                <Controller
                  name="paymentDate"
                  control={control as any}
                  render={({ field }) => <TextField {...field} type="date" />}
                />
                <Controller
                  name="type"
                  control={control as any}
                  render={({ field }) => (
                    <Select {...field} value={ReservePaymentType.Ach}>
                      <MenuItem value={ReservePaymentType.Ach}>
                        Invoice
                      </MenuItem>
                    </Select>
                  )}
                />
              </Box>
            </Grid>
          </Box>
        </FormProvider>
      </DialogContent>
      <Box className="dialog-footer">
        <Box className="button-spacing">
          <NewConvergeButton
            label="CANCEL"
            variant="outlined"
            onClick={handleClose}
          />
        </Box>
        <Box>
          <NewConvergeButton
            label={isEditing ? 'UPDATE PAYMENT' : 'ADD PAYMENT'}
            onClick={handleSubmit(handleFormSubmit)}
            loading={paymentLoading}
            disabled={errors && Object.keys(errors).length > 0}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
